import React, { useMemo, useState } from "react";
import { useQuery } from "@apollo/client/react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import Autocomplete from "@mui/material/Autocomplete";

import { CUSTOM_FIELD_TEMPLATES_QUERY } from "../../constants/graphql/queries";

const styles = {
  title: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    position: "relative",
    padding: 2,
  },
  closeButton: {
    position: "absolute",
    right: 8,
    top: 8,
  },
};

export default function AssignExistingConfigurableFieldDialog({
  projectId,
  enabledTemplateIds,
  onClose,
  onSelect,
}) {
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [assigning, setAssigning] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(true);
  const { data, loading } = useQuery(CUSTOM_FIELD_TEMPLATES_QUERY, {
    variables: {
      sortBy: "default_label",
      sortDirection: "asc",
    },
  });

  const assignableTemplates = useMemo(() => {
    const templates = data?.customFieldTemplates || [];

    return templates.filter(template => {
      const templateProjectId = template.project?.id;
      const matchesScope = !templateProjectId || String(templateProjectId) === String(projectId);
      const alreadyEnabled = enabledTemplateIds.includes(String(template.id));

      return matchesScope && !alreadyEnabled;
    });
  }, [data, enabledTemplateIds, projectId]);

  const handleAssign = async () => {
    if (!selectedTemplate) return;

    setAssigning(true);

    try {
      await onSelect(selectedTemplate);
    } finally {
      setAssigning(false);
    }
  };

  return (
    <Dialog open onClose={assigning ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={styles.title}>
        <Typography fontSize={22} fontWeight={500} color="text.primary">
          Use Existing Configurable Field
        </Typography>
        <IconButton
          edge="end"
          onClick={onClose}
          aria-label="close"
          sx={styles.closeButton}
          disabled={assigning}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Box mt={2}>
          {loading ? (
            <Box display="flex" justifyContent="center" my={3}>
              <CircularProgress />
            </Box>
          ) : assignableTemplates.length === 0 ? (
            <Typography color="text.secondary">
              No global or project-specific configurable fields are available to add.
            </Typography>
          ) : (
            <Autocomplete
              fullWidth
              options={assignableTemplates}
              getOptionLabel={option => option.defaultLabel || ""}
              value={selectedTemplate}
              open={dropdownOpen && assignableTemplates.length > 0}
              onOpen={() => setDropdownOpen(true)}
              onClose={() => setDropdownOpen(false)}
              onChange={(event, newValue) => {
                setSelectedTemplate(newValue);
                setDropdownOpen(false);
              }}
              openOnFocus
              autoHighlight
              renderInput={params => (
                <TextField
                  {...params}
                  label="Select configurable field"
                  variant="outlined"
                  autoFocus
                />
              )}
              renderOption={(props, option) => (
                <li {...props}>
                  <Box>
                    <Typography>{option.defaultLabel}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {option.project?.id ? "Project field" : "Global field"}
                    </Typography>
                  </Box>
                </li>
              )}
            />
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Box p={2} display="flex" justifyContent="flex-end" gap={1} width="100%">
          <Button variant="outlined" onClick={onClose} disabled={assigning}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleAssign}
            disabled={!selectedTemplate || assigning}
            startIcon={assigning ? <CircularProgress size={20} color="inherit" /> : null}
          >
            Add Field
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
}
